import { useState, type CSSProperties } from 'react';
import { color, type RoleTone } from '../styles/theme';
import {
  useAdmins,
  useChangeRole,
  usePermissionMatrix,
  useAdminPermissions,
  useSaveAdminPermissions,
} from '../api/queries';
import {
  useRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  isEndpointMissing,
  beyondActor,
  roleRefusal,
  ONLY_AN_OWNER,
} from '../api/access';
import type { AdminRow, NamedRole, PermissionMatrix } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PermissionGrid, samePermissions } from '../components/PermissionGrid';
import { MemberIdentity } from '../components/MemberIdentity';
import { RolePill, PreviewPill } from '../components/pills';
import {
  PageTitle,
  Card,
  CardTitle,
  ColHead,
  Loading,
  ErrorNote,
  Button,
  Field,
  Modal,
  Confirm,
  Eyebrow,
  inputStyle,
  selectStyle,
} from '../components/ui';
import { IconPlus } from '../components/icons';

/**
 * Roles & permissions — what each role can do, and which role each person holds.
 *
 * Three kinds of role share one table. Owner, Viewer and Developer are fixed
 * by the API. A named role is a set the lab defines once and gives to several
 * people — it is the thing the ENACT job titles (CRC, Program Manager, …)
 * become. Custom is one person's own boxes, for whoever fits no set.
 *
 * Pointed at an API older than the named-role routes, the table shows an
 * amber Preview pill, lists only the built-in roles — it never invents one —
 * and the New role button is disabled with a reason; everything else on the
 * page — role changes, Custom ticks — still works. See src/api/access.ts.
 */

const PHI_NOTE =
  'Performing a PHI match, and seeing matched records, is set per person — the PHI-Match column under User management — rather than by role: the act that creates PHI is granted by the PI. A Developer account can never hold it.';

const OWNER_NOTE =
  'An Owner always keeps account management, so nobody can lock the lab out of this screen.';

/** One row of the Roles table, whatever kind of role it is. */
interface RoleRow {
  key: string;
  label: string;
  blurb: string;
  permissions: string[];
  tone: RoleTone;
  /** Owner, Viewer, Developer and Custom come from the API's fixed list. */
  builtIn: boolean;
}


const ROLES_GRID = 'minmax(0,1.5fr) minmax(0,2.4fr) 80px 90px 150px';
const PEOPLE_GRID = 'minmax(0,2fr) minmax(0,1.2fr) minmax(0,1.3fr)';

const linkButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: color.blue,
  fontSize: 12.5,
  fontWeight: 700,
  cursor: 'pointer',
  padding: '6px 8px',
  borderRadius: 8,
};

const note: CSSProperties = { fontSize: 11.5, color: color.muted, margin: '6px 0 0', maxWidth: 660 };

const selectedRow: CSSProperties = {
  background: color.tint,
  boxShadow: `inset 3px 0 0 ${color.blue}`,
};

export function Roles() {
  const { admin: me, can, refresh } = useAuth();
  const manage = can('admins:manage');
  const meOwner = me?.role === 'owner';
  const matrix = usePermissionMatrix();
  const admins = useAdmins();
  const roles = useRoles();
  const changeRole = useChangeRole();
  const del = useDeleteRole();

  /** Which role the reference card is showing. */
  const [selected, setSelected] = useState<string | null>(null);
  /** The editor, open on an existing role or on nothing (a new one). */
  const [editing, setEditing] = useState<{ role: NamedRole | null } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  /** Whose boxes the People table has opened underneath their row. */
  const [expanded, setExpanded] = useState<string | null>(null);
  /**
   * A role picked from someone's dropdown, waiting for the confirmation
   * (15 Oct 2026): a change signs them out everywhere and moves what they
   * can reach, so it is asked once rather than applied on the pick.
   */
  const [pendingRole, setPendingRole] = useState<{ id: string; role: string } | null>(null);

  if (matrix.isLoading || admins.isLoading) return <Loading />;
  const failed = matrix.error ?? admins.error;
  if (failed) return <ErrorNote message={(failed as Error).message} />;
  if (!matrix.data || !admins.data) return null;

  const m = matrix.data;
  const people = admins.data.admins;
  const preview = isEndpointMissing(roles.error);
  /** The named roles as records — for the editor, and for member counts. */
  const named: NamedRole[] = preview ? [] : (roles.data?.roles ?? []);

  const countOf = (key: string) => people.filter((a) => a.role === key).length;

  // The matrix already lists every role in screen order — the three fixed
  // sets, the named roles, Custom last — with what each allows. An older API
  // lists only the four built-ins, and that is all the preview shows.
  const rows: RoleRow[] = m.capabilities.map(
    (c): RoleRow => ({
      key: c.key,
      label: c.label,
      blurb: c.blurb,
      permissions: c.key === 'custom' ? [] : c.permissions,
      tone: c.key === 'owner' ? 'owner' : !c.builtIn ? 'named' : c.key === 'custom' ? 'custom' : 'preset',
      builtIn: c.builtIn,
    }),
  );

  const rowOf = (key: string) => rows.find((r) => r.key === key) ?? null;

  /** What a role change will do, said before it is made. */
  const roleChangeDetail = (fromLabel: string, to: string): string => {
    const target = rowOf(to);
    const reach =
      to === 'owner'
        ? 'Owners can do everything, including managing accounts and roles.'
        : to === 'custom'
          ? 'Custom uses their own boxes — open their row afterwards to set them.'
          : target
            ? `They will be able to do ${target.permissions.length} of ${m.permissions.length} things.`
            : '';
    const phi = to === 'developer' ? ' A developer can never hold PHI-Match, so any they have is removed.' : '';
    return `${fromLabel} → ${target?.label ?? to}. ${reach}${phi} They are signed out now and get the new access when they sign back in.`;
  };
  const shown = selected ? rowOf(selected) : null;
  const activeOwners = people.filter((p) => p.role === 'owner' && p.status === 'active').length;

  /** Every label already in use, for the editor's clash check. */
  const takenLabels = (except: NamedRole | null) =>
    rows.filter((r) => r.key !== except?.key).map((r) => r.label.trim().toLowerCase());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <PageTitle sub="What each role can do, and which role each person holds.">
        Roles & permissions
      </PageTitle>

      {/* ── Roles ── */}
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
            padding: '20px 20px 14px',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <CardTitle style={{ marginBottom: 4 }}>Roles</CardTitle>
            <p style={{ fontSize: 12.5, color: color.muted, margin: 0, maxWidth: 680 }}>
              Owner, Viewer and Developer are fixed. A named role is a set you define once and give
              to several people; <strong>Custom</strong> is one person’s own boxes.
            </p>
          </div>
          {preview && <PreviewPill />}
          {manage && (
            <Button
              variant="navy"
              disabled={preview}
              title={preview ? 'This API does not have named roles yet.' : undefined}
              onClick={() => setEditing({ role: null })}
            >
              <IconPlus /> New role
            </Button>
          )}
        </div>

        {roles.error && !preview && (
          <div style={{ padding: '0 20px 14px' }}>
            <ErrorNote message={(roles.error as Error).message} />
          </div>
        )}

        <div className="x-scroll">
          <div style={{ minWidth: 760 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: ROLES_GRID,
                gap: 12,
                padding: '11px 20px',
                background: color.bg,
              }}
            >
              <ColHead>Role</ColHead>
              <ColHead>What it allows</ColHead>
              <ColHead>Ticks</ColHead>
              <ColHead>Members</ColHead>
              <span />
            </div>

            {rows.map((r) => {
              const on = r.key === selected;
              const members = countOf(r.key);
              const source = named.find((n) => n.key === r.key) ?? null;
              return (
                <div
                  key={r.key}
                  onClick={() => setSelected(on ? null : r.key)}
                  className="hov-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: ROLES_GRID,
                    gap: 12,
                    alignItems: 'center',
                    padding: '13px 20px',
                    borderBottom: `1px solid ${color.line}`,
                    cursor: 'pointer',
                    ...(on ? selectedRow : {}),
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <RolePill tone={r.tone} label={r.label} />
                  </span>
                  <span style={{ fontSize: 12.5, color: color.ink, minWidth: 0 }}>{r.blurb}</span>
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: color.muted,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {r.key === 'custom' ? 'per person' : `${r.permissions.length}/${m.permissions.length}`}
                  </span>
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: color.ink,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {members}
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 2,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {r.builtIn || !manage ? (
                      <span style={{ fontSize: 12, color: color.muted, padding: '6px 8px' }}>
                        {r.builtIn ? 'Built in' : ''}
                      </span>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditing({ role: source })}
                          className="hov-tint"
                          style={linkButton}
                        >
                          Edit
                        </button>
                        {members > 0 ? (
                          <span
                            style={{ fontSize: 11.5, color: color.muted, padding: '6px 8px' }}
                            title="A role someone holds cannot be deleted."
                          >
                            {members} {members === 1 ? 'member' : 'members'} — move them first
                          </span>
                        ) : (
                          <Confirm
                            open={confirmDelete === r.key}
                            title={`Delete ${r.label}?`}
                            detail="Nobody holds it, so nothing changes for anyone."
                            confirmLabel="Delete"
                            align="right"
                            onConfirm={() => {
                              setConfirmDelete(null);
                              if (selected === r.key) setSelected(null);
                              del.mutate(r.key);
                            }}
                            onCancel={() => setConfirmDelete(null)}
                          >
                            <button
                              onClick={() =>
                                setConfirmDelete(confirmDelete === r.key ? null : r.key)
                              }
                              className="hov-danger"
                              style={{ ...linkButton, color: color.muted }}
                            >
                              Delete
                            </button>
                          </Confirm>
                        )}
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {del.error && (
          <div style={{ padding: '14px 20px' }}>
            <ErrorNote message={(del.error as Error).message} />
          </div>
        )}
      </Card>

      {/* ── The reference ── */}
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div style={{ padding: '20px 20px 14px' }}>
          <CardTitle style={{ marginBottom: 4 }}>What a role allows</CardTitle>
          <p style={{ fontSize: 12.5, color: color.muted, margin: 0, maxWidth: 680 }}>
            {shown
              ? shown.key === 'custom'
                ? 'Custom has no fixed set. Open someone in the table below to see, and set, their own boxes.'
                : `${shown.label} — ${shown.permissions.length} of ${m.permissions.length} things.`
              : 'Pick a role above to see its boxes.'}
          </p>
        </div>

        {shown && shown.key !== 'custom' && (
          <div style={{ padding: '16px 20px 18px', borderTop: `1px solid ${color.line}` }}>
            <PermissionGrid
              matrix={m}
              value={shown.permissions}
              editable={false}
              subject={shown.label}
              locked={shown.key === 'owner' ? m.ownerAlways : []}
            />
            <p style={note}>{OWNER_NOTE}</p>
            <p style={note}>{PHI_NOTE}</p>
          </div>
        )}
      </Card>

      {/* ── People ── */}
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div style={{ padding: '20px 20px 14px' }}>
          <CardTitle style={{ marginBottom: 4 }}>People and their roles</CardTitle>
          <p style={{ fontSize: 12.5, color: color.muted, margin: 0, maxWidth: 680 }}>
            Change a role here. Open a row to see what that person can do; a Custom row is where
            their own boxes get set. Nobody changes their own role.
          </p>
          {manage && !meOwner && (
            <p style={{ ...note, margin: '4px 0 0' }}>
              You can give only access you have yourself, so a role that includes more is marked
              “{ONLY_AN_OWNER}”. An owner’s account is an owner’s to change.
            </p>
          )}
        </div>

        <div className="x-scroll">
          <div style={{ minWidth: 680 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: PEOPLE_GRID,
                gap: 12,
                padding: '11px 20px',
                background: color.bg,
              }}
            >
              <ColHead>Member</ColHead>
              <ColHead>Role</ColHead>
              <ColHead>What they can do</ColHead>
            </div>

            {people.map((a) => {
              const isMe = a.id === me?.id;
              // Nobody changes their own role, and an owner's account is an owner's (the API's rules).
              const ownerOnly = a.role === 'owner' && !meOwner;
              const editable = manage && !isMe && !ownerOnly;
              const row = rowOf(a.role);
              /** What they hold now, when a set says so; a Custom holder's own boxes are not loaded here. */
              const holds = a.role === 'custom' ? null : (row?.permissions ?? null);
              const refusal = (key: string) => (key === a.role ? null : roleRefusal(me, m, key, holds));
              const optionText = (r: { key: string; label: string }) =>
                refusal(r.key) ? `${r.label} (${ONLY_AN_OWNER})` : r.label;
              const open = expanded === a.id;
              const lastOwner = a.role === 'owner' && activeOwners <= 1;
              const summary =
                a.role === 'custom'
                  ? 'their own boxes'
                  : row
                    ? `uses the ${row.label} set — ${row.permissions.length} of ${m.permissions.length}`
                    : a.roleLabel;

              return (
                <div key={a.id}>
                  <div
                    onClick={() => setExpanded(open ? null : a.id)}
                    className="hov-row"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: PEOPLE_GRID,
                      gap: 12,
                      alignItems: 'center',
                      padding: '13px 20px',
                      borderBottom: `1px solid ${color.line}`,
                      cursor: 'pointer',
                      ...(open ? selectedRow : {}),
                    }}
                  >
                    <MemberIdentity
                      name={a.name}
                      email={a.email}
                      status={a.status}
                      isMe={isMe}
                      lastLoginAt={a.lastLoginAt}
                    />

                    {/* The control stops the click so changing a role does not also open the row. */}
                    <span style={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                      {editable ? (
                        <Confirm
                          open={pendingRole?.id === a.id}
                          title={`Change ${a.name}’s role to ${rowOf(pendingRole?.role ?? '')?.label ?? pendingRole?.role ?? ''}?`}
                          detail={pendingRole?.id === a.id ? roleChangeDetail(a.roleLabel, pendingRole.role) : undefined}
                          confirmLabel="Change role"
                          onConfirm={() => {
                            if (pendingRole) changeRole.mutate(pendingRole);
                            setPendingRole(null);
                          }}
                          onCancel={() => setPendingRole(null)}
                        >
                          <select
                            // Shows the pick while it waits; back to the stored role on cancel.
                            value={pendingRole?.id === a.id ? pendingRole.role : a.role}
                            disabled={lastOwner || changeRole.isPending}
                            title={lastOwner ? 'The last owner cannot be demoted.' : undefined}
                            aria-label={`${a.name}: role`}
                            onChange={(e) =>
                              e.target.value !== a.role
                                ? setPendingRole({ id: a.id, role: e.target.value })
                                : setPendingRole(null)
                            }
                            style={{
                              ...selectStyle,
                              borderRadius: 999,
                              padding: '6px 26px 6px 14px',
                              fontSize: 12.5,
                            }}
                          >
                            <optgroup label="Fixed">
                              {rows
                                .filter((r) => r.builtIn && r.key !== 'custom')
                                .map((r) => (
                                  <option key={r.key} value={r.key} disabled={!!refusal(r.key)}>
                                    {optionText(r)}
                                  </option>
                                ))}
                            </optgroup>
                            {rows.some((r) => !r.builtIn) && (
                              <optgroup label="Named roles">
                                {rows
                                  .filter((r) => !r.builtIn)
                                  .map((r) => (
                                    <option key={r.key} value={r.key} disabled={!!refusal(r.key)}>
                                      {optionText(r)}
                                    </option>
                                  ))}
                              </optgroup>
                            )}
                            <optgroup label="One person">
                              <option value="custom">Custom</option>
                            </optgroup>
                          </select>
                        </Confirm>
                      ) : (
                        <span
                          title={
                            manage && isMe
                              ? 'You cannot change your own role. Ask an owner.'
                              : manage && ownerOnly
                                ? 'Only an owner can change an owner’s account.'
                                : undefined
                          }
                        >
                          <RolePill tone={row?.tone ?? 'named'} label={a.roleLabel} />
                        </span>
                      )}
                    </span>

                    <span style={{ fontSize: 12.5, color: color.muted, minWidth: 0 }}>{summary}</span>
                  </div>

                  {open && (
                    <PersonPanel admin={a} matrix={m} role={row} editable={editable} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {changeRole.error && (
          <div style={{ padding: '14px 20px' }}>
            <ErrorNote message={(changeRole.error as Error).message} />
          </div>
        )}
      </Card>

      {editing && (
        <RoleEditor
          matrix={m}
          role={editing.role}
          taken={takenLabels(editing.role)}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setSelected(saved.key);
            // Editing the role you hold changes what you can do right now.
            if (me?.role === saved.key) void refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * What one person can do, opened under their row.
 *
 * Read-only for anyone on a fixed or named role — their set is the role's.
 * A Custom account gets its own boxes here, saved on demand so a half-made
 * change can be discarded.
 */
function PersonPanel({
  admin,
  matrix,
  role,
  editable,
}: {
  admin: AdminRow;
  matrix: PermissionMatrix;
  role: RoleRow | null;
  editable: boolean;
}) {
  const { admin: me } = useAuth();
  const isCustom = admin.role === 'custom';
  const stored = useAdminPermissions(isCustom ? admin.id : null);
  const save = useSaveAdminPermissions();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const panel: CSSProperties = {
    padding: '14px 20px 18px',
    borderBottom: `1px solid ${color.line}`,
    background: color.bg,
  };

  if (!isCustom) {
    return (
      <div style={panel}>
        <p style={{ fontSize: 12.5, color: color.muted, margin: '0 0 10px', maxWidth: 640 }}>
          Fixed by the {role?.label ?? admin.roleLabel} role. To change what this person alone can
          do, set them to <strong>Custom</strong>.
        </p>
        <PermissionGrid
          matrix={matrix}
          value={role?.permissions ?? []}
          editable={false}
          subject={admin.name}
          locked={admin.role === 'owner' ? matrix.ownerAlways : []}
        />
      </div>
    );
  }

  if (stored.isLoading) return <Loading label="Loading their boxes…" />;
  if (stored.error) return <ErrorNote message={(stored.error as Error).message} />;

  const base = stored.data?.permissions ?? [];
  const value = draft ?? base;
  const dirty = draft !== null && !samePermissions(draft, base);

  async function saveAll() {
    if (!draft) return;
    setError(null);
    try {
      await save.mutateAsync({ id: admin.id, permissions: draft });
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div style={panel}>
      <p style={{ fontSize: 12.5, color: color.muted, margin: '0 0 10px' }}>
        {admin.name} can do {value.length} of {matrix.permissions.length} things.
        {!editable && ' Fixed from here.'}
      </p>
      <PermissionGrid
        matrix={matrix}
        value={value}
        editable={editable}
        onChange={(next) => {
          setDraft(next);
          setSavedAt(null);
        }}
        subject={admin.name}
        // What they already hold is not being given; anything new only from what you hold.
        withheld={beyondActor(me, matrix.permissions.map((p) => p.key)).filter((k) => !base.includes(k))}
      />
      {editable && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 8,
            minHeight: 40,
          }}
        >
          <Button variant="navy" disabled={!dirty || save.isPending} onClick={() => void saveAll()}>
            {save.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </Button>
          {dirty && (
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
              style={{ ...linkButton, color: color.muted, padding: 0 }}
            >
              Discard
            </button>
          )}
          {savedAt && !dirty && (
            <span style={{ fontSize: 12.5, color: color.green, fontWeight: 700 }}>
              Saved — it applies from the next thing they do.
            </span>
          )}
          {error && <ErrorNote message={error} />}
        </div>
      )}
    </div>
  );
}

/** Create or edit a named role: a name, one line on what it is for, and the boxes. */
function RoleEditor({
  matrix,
  role,
  taken,
  onClose,
  onSaved,
}: {
  matrix: PermissionMatrix;
  /** Null makes a new role. */
  role: NamedRole | null;
  /** Every other role's label, lower-cased, so a clash is caught before the round trip. */
  taken: string[];
  onClose: () => void;
  onSaved: (role: NamedRole) => void;
}) {
  const { admin: me } = useAuth();
  const create = useCreateRole();
  const update = useUpdateRole();
  const [label, setLabel] = useState(role?.label ?? '');
  const [blurb, setBlurb] = useState(role?.blurb ?? '');
  const [permissions, setPermissions] = useState<string[]>(role?.permissions ?? []);
  const [error, setError] = useState<string | null>(null);

  const name = label.trim();
  const problem = !name
    ? 'Give the role a name.'
    : taken.includes(name.toLowerCase())
      ? 'That name is already a role.'
      : permissions.length === 0
        ? 'Tick at least one box.'
        : null;
  const pending = create.isPending || update.isPending;

  async function save() {
    if (problem) return;
    setError(null);
    try {
      const res = role
        ? await update.mutateAsync({
            key: role.key,
            patch: { label: name, blurb: blurb.trim(), permissions },
          })
        : await create.mutateAsync({ label: name, blurb: blurb.trim() || undefined, permissions });
      onSaved(res.role);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal
      label={role ? 'Edit role' : 'New role'}
      width={760}
      onClose={onClose}
      header={
        <div>
          <CardTitle style={{ marginBottom: 4 }}>{role ? `Edit ${role.label}` : 'New role'}</CardTitle>
          <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
            A set of boxes you define once and give to several people.
          </p>
        </div>
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button
            variant="navy"
            disabled={!!problem || pending}
            title={problem ?? undefined}
            onClick={() => void save()}
          >
            {pending ? 'Saving…' : role ? 'Save changes' : 'Create role'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {problem && name && (
            <span style={{ fontSize: 12.5, color: color.muted, fontWeight: 600 }}>{problem}</span>
          )}
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Name">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={60}
            placeholder="e.g. Clinical Research Coordinator"
            autoFocus
            style={inputStyle}
          />
        </Field>
        <Field label="What it is for" hint="(optional)">
          <input
            value={blurb}
            onChange={(e) => setBlurb(e.target.value)}
            maxLength={200}
            placeholder="One line, shown beside the role"
            style={inputStyle}
          />
        </Field>
        <div>
          <Eyebrow>What it allows</Eyebrow>
          <PermissionGrid
            matrix={matrix}
            value={permissions}
            editable
            onChange={setPermissions}
            subject={name || 'This role'}
            withheld={beyondActor(me, matrix.permissions.map((p) => p.key)).filter(
              (k) => !((role?.permissions ?? []) as readonly string[]).includes(k),
            )}
          />
        </div>
        <p style={note}>{PHI_NOTE}</p>
        {error && <ErrorNote message={error} />}
      </div>
    </Modal>
  );
}
